import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { selectCustomer, setCustomer } from './customerSlice';
import { FormikErrors, useFormik } from 'formik';
import CustomerAPI from '../../API/customerAPI';
import { selectUser } from '../user/userSlice';
import GooglePlace from '../../components/GooglePlace';

interface Props {
  onClose: () => void;
}
export default function ShippingAddressForm({ onClose }: Props) {
  const streetRef = useRef<any>()
  const townRef = useRef<any>()
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const user = useSelector(selectUser);
  const customer = useSelector(selectCustomer);

  interface FormValues {
    first_name: string;
    last_name: string;
    address_1: string;
    address_2?: string;
    city: string;
    country: string;
    state: string;
    postcode: string;
  }
  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};
    if (!values.first_name) {
      errors.first_name = "Required";
    }
    if (!values.last_name) {
      errors.last_name = "Required";
    }
    if (!values.address_1) {
      errors.address_1 = "Required";
    }
    if (!values.city) {
      errors.city = "Required";
    }
    if (!values.state) {
      errors.state = "Required";
    }
    if (!values.postcode) {
      errors.postcode = "Required";
    } else if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.postcode)) {
      errors.postcode = "Invalid"
    }
    return errors;
  }

  const formik = useFormik({
    initialValues: {
      first_name: customer?.shipping?.first_name,
      last_name: customer?.shipping?.last_name,
      address_1: customer?.shipping?.address_1,
      address_2: customer?.shipping?.address_2,
      city: customer?.shipping?.city,
      country: "US",
      state: customer?.shipping?.state,
      postcode: customer?.shipping?.postcode
    },
    validate,
    onSubmit: values => {
      setErrorMsg('');
      setBusy(true);
      CustomerAPI.updateCustomerShipping({
        customer_id: customer?.id,
        token: user.token,
        shipping: { ...values }
      }).then((response) => {
        if ('code' in response) {
          console.error(response);
          setErrorMsg("An error has occured. Please try again later.");
        } else if ('id' in response) {
          dispatch(setCustomer(response));
          onClose();
        }
      }).catch((e) => {
        setErrorMsg(e.message);
      }).finally(() => {
        setBusy(false);
      });
    }
  });

  return (
    <Card className="shipping-address-form">
      <Card.Header><span className="fs-4">Shipping Address</span></Card.Header>
      <Card.Body>
        <Form onSubmit={formik.handleSubmit}>
          {errorMsg && (
            <p className='text-danger'>{errorMsg}</p>)}
          <div className="row">
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  id="first_name"
                  type="text"
                  isValid={Boolean(formik.values.first_name) &&
                    !Boolean(formik.errors.first_name)}
                  isInvalid={Boolean(formik.errors.first_name)}
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                />
                {formik.errors.first_name &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.first_name}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  id="last_name"
                  type="text"
                  isValid={Boolean(formik.values.last_name) &&
                    !Boolean(formik.errors.last_name)}
                  isInvalid={Boolean(formik.errors.last_name)}
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                />
                {formik.errors.last_name &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.last_name}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3 form-group required">
            <Form.Label>Street address</Form.Label>
            <GooglePlace formik={formik} streetRef={streetRef} townRef={townRef} name={'address_1'}
              city={'city'} state={'state'} postcode={'postcode'}
            />
            {formik.errors.address_1 &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.address_1}
              </Form.Control.Feedback>}
          </Form.Group>
          <Form.Group className="mb-3 form-group">
            <Form.Control
              id="address_2"
              type="text"
              placeholder="Apartment, suite, unit etc. (optional)"
              isInvalid={Boolean(formik.errors.address_2)}
              value={formik.values.address_2}
              onChange={formik.handleChange}
            />
            {formik.errors.address_2 &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.address_2}
              </Form.Control.Feedback>}
          </Form.Group>
          <Form.Group className="mb-3 form-group required">
            <Form.Label>Town / City</Form.Label>
            <Form.Control
              id="city"
              ref={townRef}
              type="text"
              name="city"
              placeholder="Enter Town/Area"
              value={formik.values.city}

            />
            {formik.errors.city &&
              <Form.Control.Feedback type="invalid">
                {formik.errors.city}
              </Form.Control.Feedback>}
          </Form.Group>
          <p>Delivery is to Residential addresses only. Non-residential deliveries
            will be canceled and are not refundable.
          </p>
          <div className="row">
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>State</Form.Label>
                <Form.Select
                  id="state"
                  isInvalid={Boolean(formik.errors.state)}
                  value={formik.values.state}
                  onChange={formik.handleChange}
                >
                  <option value="">-- select a state --</option>
                  <option value="DC">District of Columbia</option>
                  <option value="DE">Delaware</option>
                  <option value="MD">Maryland</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="VA">Virginia</option>
                  <option value="WV">West Virginia</option>
                </Form.Select>
                {formik.errors.state &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.state}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
            <div className="col">
              <Form.Group className="mb-3 form-group required">
                <Form.Label>Postcode / ZIP</Form.Label>
                <Form.Control
                  id="postcode"
                  type="text"
                  maxLength={10}
                  placeholder="Enter Postcode / ZIP"
                  value={formik.values.postcode}
                  onChange={formik.handleChange}
                />
                {formik.errors.postcode &&
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.postcode}
                  </Form.Control.Feedback>}
              </Form.Group>
            </div>
          </div>
          <Button className="me-2" variant="outline-dark" onClick={onClose}>Cancel</Button>
          {busy ?
            <Button className='bg-black' size="lg" disabled>
              <Spinner animation="border" as="span" size="sm" />
              &nbsp;&nbsp;Saving ...
            </Button>
            :
            <Button className='bg-black' type="submit">Save address</Button>
          }
        </Form>
      </Card.Body>
    </Card>
  );
}
